import {
  type AppBuild,
  type Candidate,
  type CandidateFilters,
  type CandidatePage,
  type CandidatePageQuery,
  type CandidateLogEntry,
  type CandidateStatus,
  type CompanyClassList,
  type CompanyClassSummary,
  type ControllingOverview,
  type EmployerGroup,
  type EmployerPage,
  type EmployerRef,
  type EmployerSort,
  type Job,
  type JobCostDetail,
  type JobCreateInput,
  type JobFilters,
  type JobStatus,
  type JobSummary,
  type Role,
  type PlaceSuggestion,
  type RestartPreview,
  type SourcingRun,
  type User,
} from "@vidario/domain";

import { candidatePageParams } from "./candidate-page-params";

/** Payload for inviting a new user. Company optional (admins have none);
 * role defaults to recruiter on the backend. */
export interface InviteUserInput {
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  company?: string | null;
}

/** Payload for PATCH /me/. Only `company` is editable (and only by
 * admins on the backend); name + email + role are admin-managed. */
export interface UpdateMeInput {
  company?: string | null;
}

/** Payload for PATCH /me/whats-new/: Release Note ids to add to the
 * caller's own seen and/or notified sets (ADR 0005). Both only grow. */
export interface WhatsNewMarks {
  seenWhatsNew?: string[];
  notifiedWhatsNew?: string[];
}

/** Response shape for `inviteUser`. `existing=true` means the email was
 * already in the system and a fresh invitation link was re-sent. */
export interface InviteUserResult {
  user: User;
  existing: boolean;
}

/** Response shape for `deleteUser`. `reassignedJobCount` lets the UI tell
 * the actor how many of the deleted user's jobs they just inherited. */
export interface DeleteUserResult {
  reassignedJobCount: number;
}

export type CandidatePatch = Partial<
  Pick<
    Candidate,
    // Recruiter + admin editable
    | "recruiterNotes"
    | "recommendationText"
    | "recommendationRank"
    | "skillMatch"
    // Admin-only notes
    | "internalNotes"
    | "interviewNotes"
    | "interviewKeyFindings"
    // Admin-only profile edits (contact data, attachments, basics)
    | "name"
    | "currentTitle"
    | "currentCompany"
    | "location"
    | "linkedinUrl"
    | "xingUrl"
    | "email"
    | "phone"
    | "cvUrl"
    | "photoUrl"
    | "availableFrom"
    | "salaryExpectation"
    | "source"
  >
>;

/** Job fields extracted from a posting URL/PDF — pre-fills the create form. */
export interface JobParseResult {
  values: Partial<JobCreateInput>;
}

/**
 * Canonical endpoint surface. Every consumer (queries, mutations, MSW
 * handlers) speaks the same contract. The fetch implementation talks to
 * the Django backend, which is REST-idiomatic snake_case; this adapter
 * translates keys to/from the camelCase domain model.
 */
export interface VidarioClient {
  /** Public `/health/`: which release (production) or commit is running. */
  getHealth: () => Promise<AppBuild>;
  getMe: () => Promise<User>;
  /** Self-service profile update. Backend enforces admin-only for the
   * `company` field; everything else stays admin-managed. */
  updateMe: (input: UpdateMeInput) => Promise<User>;
  markWhatsNew: (marks: WhatsNewMarks) => Promise<User>;
  getUsers: () => Promise<User[]>;
  /** Admin-only. Idempotent: if the email is already in the system,
   * re-sends the invitation link and returns `existing: true`. */
  inviteUser: (input: InviteUserInput) => Promise<InviteUserResult>;
  /** Admin-only: how long an Invitation link stays valid (#21). */
  getInviteSettings: () => Promise<{ invitationLinkHours: number }>;
  /** Super-admin only. Hard-deletes the user; their jobs are reassigned
   * to the caller (Job.created_by has PROTECT). The result carries the
   * number of reassigned jobs so the UI can word a follow-up toast. */
  deleteUser: (id: string) => Promise<DeleteUserResult>;
  /** Admin-only: assign a user's total search quota (absolute value).
   * Returns the updated user (with recomputed `searchesRemaining`). */
  setUserQuota: (input: { id: string; searchQuota: number }) => Promise<User>;

  getJobs: (filters?: JobFilters) => Promise<Job[]>;
  getJobsWithStats: () => Promise<JobSummary[]>;
  getJob: (id: string) => Promise<Job>;
  /** Saves a Draft; only `startSourcing` takes a job out of Draft (#16). */
  createJob: (input: JobCreateInput) => Promise<Job>;
  updateJob: (id: string, input: JobCreateInput) => Promise<Job>;
  updateJobStatus: (input: { id: string; status: JobStatus }) => Promise<Job>;
  /** Admin-only: release the recommendation. Moves the job to Empfehlung,
   * makes the recommendation visible to the recruiter and emails them
   * (ADR 0004). */
  releaseRecommendation: (jobId: string) => Promise<Job>;
  /** Admin-only: replace the set of additional recruiters on a job. The
   * owner (`createdBy`) is never part of this list. */
  setJobRecruiters: (input: { id: string; userIds: string[] }) => Promise<Job>;
  /** Auto-import: extract job fields from a posting URL or an uploaded PDF. */
  parseJob: (source: { url: string } | { file: File }) => Promise<JobParseResult>;

  getCandidates: (jobId: string, filters?: CandidateFilters) => Promise<Candidate[]>;
  /** One page of the candidate page, filtered and sorted by the server (#14). */
  getCandidatePage: (jobId: string, query: CandidatePageQuery) => Promise<CandidatePage>;
  getCandidate: (jobId: string, candidateId: string) => Promise<Candidate>;
  updateCandidate: (input: { candidateId: string; patch: CandidatePatch }) => Promise<Candidate>;
  updateCandidateStatus: (input: {
    candidateId: string;
    status: CandidateStatus;
    rejectionReason?: string | null;
    /** For a move back to the Longlist (#162); goes into the history. */
    reason?: string | null;
  }) => Promise<Candidate>;
  getCandidateLog: (candidateId: string) => Promise<CandidateLogEntry[]>;
  /** Upload a CV (PDF) or photo (jpg/png/webp) for a candidate. Returns
   * the updated candidate with `cvUrl` / `photoUrl` populated. */
  uploadCandidateAttachment: (input: {
    candidateId: string;
    kind: "cv" | "photo";
    file: File;
  }) => Promise<Candidate>;

  /** Start the backend sourcing pipeline for a job ("An Vidario senden"). */
  startSourcing: (jobId: string) => Promise<SourcingRun>;
  /** Admin-only: stop the job's running search; it keeps what it found (#88). */
  stopSourcing: (jobId: string) => Promise<SourcingRun>;
  /** Latest sourcing run for a job, or null if none has run yet. */
  getSourcingRun: (jobId: string) => Promise<SourcingRun | null>;
  /** Admin-only: what a Search restart would delete, by group (#18). */
  getRestartPreview: (jobId: string) => Promise<RestartPreview>;
  /** Real places for the Standort while it's typed (#49); a 503 means the
   * place provider is down — the Standort can still be typed and saved. */
  suggestPlaces: (query: string, signal?: AbortSignal) => Promise<PlaceSuggestion[]>;

  /** Admin-only Controlling: per-job API-cost roll-ups + the
   * unattributed bucket. */
  getControllingOverview: () => Promise<ControllingOverview>;
  /** Admin-only Controlling: one job's cost breakdown, activities →
   * Posten. */
  getControllingJob: (jobId: number) => Promise<JobCostDetail>;

  /** Admin-only Anonym-Ansicht audit trail (#140): switching it, and opening clear data in it. */
  logHideModeSwitch: (turnedOn: boolean) => Promise<void>;
  logCandidateOpened: (input: {
    candidateId: string;
    action: "source_profile_opened" | "attachment_opened";
    kind: "linkedin" | "xing" | "cv" | "photo";
  }) => Promise<void>;

  /** Admin-only company classes (#139). */
  getCompanyClasses: () => Promise<CompanyClassList>;
  createCompanyClass: (name: string) => Promise<CompanyClassSummary>;
  renameCompanyClass: (input: { id: number; name: string }) => Promise<CompanyClassSummary>;
  deleteCompanyClass: (id: number) => Promise<void>;
  getClassCompanies: (classId: number) => Promise<EmployerGroup[]>;
  /** The to-do list, one page of 50. */
  getUnassignedCompanies: (query: UnassignedQuery) => Promise<EmployerPage>;
  /** Into a class, or back onto the to-do list (`classId: null`). */
  assignCompanies: (input: { items: EmployerRef[]; classId: number | null }) => Promise<void>;
  mergeCompanies: (input: {
    items: EmployerRef[];
    main: EmployerRef;
  }) => Promise<{ companyId: number; name: string }>;
}

export interface UnassignedQuery {
  q: string;
  sort: EmployerSort;
  page: number;
}

// --- snake_case <-> camelCase translation -----------------------------

/** JSON fields whose *values* are free-form maps (skill names, score
 * dimensions) and must NOT have their inner keys rewritten — e.g. a skill
 * named "TypeScript" would otherwise be mangled to "type_script". */
const OPAQUE_KEYS = new Set(["skill_match", "skillMatch", "sub_scores", "subScores"]);

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function deepMapKeys(value: unknown, mapKey: (k: string) => string, opaque = false): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => deepMapKeys(item, mapKey, opaque));
  }
  if (value !== null && typeof value === "object") {
    if (opaque) return value;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[mapKey(k)] = deepMapKeys(v, mapKey, OPAQUE_KEYS.has(k));
    }
    return out;
  }
  return value;
}

/** What users read when the backend gave no reason (#22). Status 0 means
 * the request never reached the server. */
function statusMessage(status: number): string {
  if (status === 0) return "Keine Verbindung zum Server.";
  if (status === 403) return "Keine Berechtigung.";
  if (status === 404) return "Nicht gefunden.";
  if (status >= 500) return "Serverfehler — bitte später erneut versuchen.";
  return "Die Anfrage wurde abgelehnt.";
}

interface Reasons {
  message: string | null;
  fieldErrors: Record<string, string[]>;
}

/** The backend's reasons: drf-standardized-errors (`errors[]` with `detail`
 * and the field in `attr`), or a plain `{"detail": "…"}`. */
function readReasons(body: string, mapAttr: (attr: string) => string): Reasons {
  let parsed: { detail?: unknown; errors?: { detail?: unknown; attr?: unknown }[] };
  try {
    parsed = JSON.parse(body) as typeof parsed;
  } catch {
    return { message: null, fieldErrors: {} };
  }
  const fieldErrors: Record<string, string[]> = {};
  const details: string[] = [];
  for (const e of parsed?.errors ?? []) {
    if (typeof e.detail !== "string" || !e.detail.trim()) continue;
    const detail = e.detail.trim();
    details.push(detail);
    if (typeof e.attr === "string" && e.attr) (fieldErrors[mapAttr(e.attr)] ??= []).push(detail);
  }
  if (details.length === 0 && typeof parsed?.detail === "string" && parsed.detail) {
    details.push(parsed.detail);
  }
  return { message: details.length > 0 ? details.join(" ") : null, fieldErrors };
}

/** A failed request. `message` is what users should read (#22): the
 * backend's German reason, or a status-based fallback. `fieldErrors` holds
 * the reasons per form field. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly body: string;
  public readonly fieldErrors: Record<string, string[]>;
  constructor(status: number, body: string, mapAttr: (attr: string) => string = (a) => a) {
    const reasons = readReasons(body, mapAttr);
    super(reasons.message ?? statusMessage(status));
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.fieldErrors = reasons.fieldErrors;
  }
}

/** The message to show for any thrown error. */
export function apiErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Unbekannter Fehler.";
}

/** Typed-fetch client. Paths carry trailing slashes to match Django's URL
 * routing (APPEND_SLASH would 301 a POST otherwise). Auth rides on HttpOnly
 * cookies — hence `credentials: "include"`.
 *
 * `mapKeys` translates camelCase domain keys to/from the wire: enable it
 * for the snake_case Django backend, disable it for the camelCase MSW
 * mocks (which validate against the domain Zod schemas directly).
 *
 * `onAuthExpired` fires when a 401 survives the refresh: the session itself
 * is gone, and the app should send the user to the login page (#23). */
export function createFetchClient(options: {
  baseUrl: string;
  fetch?: typeof fetch;
  mapKeys?: boolean;
  onAuthExpired?: () => void;
}): VidarioClient {
  const { baseUrl, fetch: fetchFn = fetch, mapKeys = true, onAuthExpired } = options;
  const toWire = (v: unknown): unknown => (mapKeys ? deepMapKeys(v, camelToSnake) : v);
  const fromWire = (v: unknown): unknown => (mapKeys ? deepMapKeys(v, snakeToCamel) : v);

  const REFRESH_PATH = "/cookies-token-refresh/";
  // De-dupes concurrent refreshes: a burst of 401s must not race the
  // rotating refresh cookie (parallel refreshes could invalidate each
  // other). All callers await the same in-flight refresh.
  let refreshInFlight: Promise<boolean> | null = null;

  function sendRequest(
    path: string,
    method: string,
    body: unknown,
    hasBody: boolean,
    isFormData: boolean,
    signal?: AbortSignal,
  ): Promise<Response> {
    const sent = fetchFn(`${baseUrl}${path}`, {
      method,
      credentials: "include",
      ...(signal && { signal }),
      headers: {
        Accept: "application/json",
        ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
      },
      ...(hasBody ? { body: isFormData ? (body as FormData) : JSON.stringify(toWire(body)) } : {}),
    });
    // Offline or unreachable: an ApiError like any other, so it reads the same (#22).
    return sent.catch(() => {
      throw new ApiError(0, "");
    });
  }

  /** Attempt one cookie-based token refresh via `/cookies-token-refresh/`.
   * Resolves true iff it succeeded. De-duplicated across concurrent calls. */
  function tryRefresh(): Promise<boolean> {
    refreshInFlight ??= fetchFn(`${baseUrl}${REFRESH_PATH}`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
    return refreshInFlight;
  }

  async function request<T>(
    path: string,
    init?: { method?: string; body?: unknown; signal?: AbortSignal },
  ): Promise<T> {
    const { method = "GET", body, signal } = init ?? {};
    const hasBody = body !== undefined;
    // FormData (PDF upload) rides as-is: the browser sets the multipart
    // Content-Type with boundary, and the body must not be JSON-stringified.
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

    let res = await sendRequest(path, method, body, hasBody, isFormData, signal);
    // A 401 usually means the short-lived access-token cookie expired while
    // the session is still valid (the refresh cookie lives much longer).
    // DRF authenticates before the view runs, so the original request had no
    // side effects — refresh once and replay it. Never recurse on refresh.
    if (res.status === 401 && path !== REFRESH_PATH && (await tryRefresh())) {
      res = await sendRequest(path, method, body, hasBody, isFormData, signal);
    }
    if (!res.ok) {
      if (res.status === 401 && path !== REFRESH_PATH) onAuthExpired?.();
      const text = await res.text().catch(() => "");
      throw new ApiError(res.status, text, mapKeys ? snakeToCamel : undefined);
    }
    if (res.status === 204) return undefined as T;
    const json = (await res.json()) as unknown;
    return fromWire(json) as T;
  }

  function qs(filters?: JobFilters | CandidateFilters | Record<string, unknown>): string {
    if (!filters) return "";
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
      if (v === undefined || v === null || v === "") continue;
      const key = mapKeys ? camelToSnake(k) : k;
      if (Array.isArray(v)) v.forEach((item) => params.append(key, String(item)));
      else params.set(key, String(v));
    }
    const s = params.toString();
    return s ? `?${s}` : "";
  }

  return {
    getHealth: () => request<AppBuild>("/health/"),
    getMe: () => request<User>("/me/"),
    updateMe: (input) =>
      request<User>("/me/", {
        method: "PATCH",
        body: input,
      }),
    markWhatsNew: (marks) => request<User>("/me/whats-new/", { method: "PATCH", body: marks }),
    getUsers: () => request<User[]>("/users/"),
    inviteUser: (input) =>
      request<InviteUserResult>("/users/invite/", {
        method: "POST",
        body: input,
      }),
    getInviteSettings: () => request<{ invitationLinkHours: number }>("/users/invite/"),
    deleteUser: (id) =>
      request<DeleteUserResult>(`/users/${encodeURIComponent(id)}/`, {
        method: "DELETE",
      }),
    setUserQuota: ({ id, searchQuota }) =>
      request<User>(`/users/${encodeURIComponent(id)}/`, {
        method: "PATCH",
        body: { searchQuota },
      }),
    getJobs: (filters) => request<Job[]>(`/jobs/${qs(filters)}`),
    getJobsWithStats: () => request<JobSummary[]>("/jobs/summary/"),
    getJob: (id) => request<Job>(`/jobs/${encodeURIComponent(id)}/`),
    createJob: (input) => request<Job>("/jobs/", { method: "POST", body: input }),
    updateJob: (id, input) =>
      request<Job>(`/jobs/${encodeURIComponent(id)}/`, { method: "PATCH", body: input }),
    updateJobStatus: ({ id, status }) =>
      request<Job>(`/jobs/${encodeURIComponent(id)}/status/`, {
        method: "PATCH",
        body: { status },
      }),
    releaseRecommendation: (jobId) =>
      request<Job>(`/jobs/${encodeURIComponent(jobId)}/release-recommendation/`, {
        method: "POST",
      }),
    setJobRecruiters: ({ id, userIds }) =>
      request<Job>(`/jobs/${encodeURIComponent(id)}/recruiters/`, {
        method: "PATCH",
        body: { user_ids: userIds.map((u) => Number(u)) },
      }),
    parseJob: (source) => {
      if ("file" in source) {
        const form = new FormData();
        form.append("file", source.file);
        return request<JobParseResult>("/jobs/parse/", {
          method: "POST",
          body: form,
        });
      }
      return request<JobParseResult>("/jobs/parse/", {
        method: "POST",
        body: { url: source.url },
      });
    },
    getCandidates: (jobId, filters) =>
      request<Candidate[]>(`/jobs/${encodeURIComponent(jobId)}/candidates/${qs(filters)}`),
    getCandidatePage: (jobId, query) =>
      request<CandidatePage>(
        `/jobs/${encodeURIComponent(jobId)}/candidates/${qs(candidatePageParams(query))}`,
      ),
    getCandidate: (jobId, candidateId) =>
      request<Candidate>(
        `/jobs/${encodeURIComponent(jobId)}/candidates/${encodeURIComponent(candidateId)}/`,
      ),
    updateCandidate: ({ candidateId, patch }) =>
      request<Candidate>(`/candidates/${encodeURIComponent(candidateId)}/`, {
        method: "PATCH",
        body: patch,
      }),
    updateCandidateStatus: ({ candidateId, status, rejectionReason, reason }) =>
      request<Candidate>(`/candidates/${encodeURIComponent(candidateId)}/status/`, {
        method: "PATCH",
        body: { status, rejectionReason: rejectionReason ?? null, reason: reason ?? null },
      }),
    getCandidateLog: (candidateId) =>
      request<CandidateLogEntry[]>(`/candidates/${encodeURIComponent(candidateId)}/log/`),
    uploadCandidateAttachment: ({ candidateId, kind, file }) => {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      return request<Candidate>(`/candidates/${encodeURIComponent(candidateId)}/attachment/`, {
        method: "POST",
        body: fd,
      });
    },
    startSourcing: (jobId) =>
      request<SourcingRun>(`/jobs/${encodeURIComponent(jobId)}/sourcing/`, {
        method: "POST",
      }),
    stopSourcing: (jobId) =>
      request<SourcingRun>(`/jobs/${encodeURIComponent(jobId)}/sourcing/stop/`, { method: "POST" }),
    getSourcingRun: (jobId) =>
      request<SourcingRun | null>(`/jobs/${encodeURIComponent(jobId)}/sourcing/`).then(
        (run) => run ?? null,
      ),
    getRestartPreview: (jobId) =>
      request<RestartPreview>(`/jobs/${encodeURIComponent(jobId)}/sourcing/restart-preview/`),
    suggestPlaces: (query, signal) =>
      request<PlaceSuggestion[]>(
        `/jobs/places/${qs({ q: query })}`,
        signal ? { signal } : undefined,
      ),
    getControllingOverview: () => request<ControllingOverview>("/controlling/jobs/"),
    getControllingJob: (jobId) =>
      request<JobCostDetail>(`/controlling/jobs/${encodeURIComponent(String(jobId))}/`),
    logHideModeSwitch: (turnedOn) =>
      request<void>("/me/hide-mode/", { method: "POST", body: { turnedOn } }),
    logCandidateOpened: ({ candidateId, action, kind }) =>
      request<void>(`/candidates/${encodeURIComponent(candidateId)}/log/`, {
        method: "POST",
        body: { action, kind },
      }),
    getCompanyClasses: () => request<CompanyClassList>("/companies/classes/"),
    createCompanyClass: (name) =>
      request<CompanyClassSummary>("/companies/classes/", { method: "POST", body: { name } }),
    renameCompanyClass: ({ id, name }) =>
      request<CompanyClassSummary>(`/companies/classes/${id}/`, {
        method: "PATCH",
        body: { name },
      }),
    deleteCompanyClass: (id) => request<void>(`/companies/classes/${id}/`, { method: "DELETE" }),
    getClassCompanies: (classId) =>
      request<EmployerGroup[]>(`/companies/classes/${classId}/companies/`),
    getUnassignedCompanies: ({ q, sort, page }) =>
      request<EmployerPage>(`/companies/unassigned/${qs({ q, sort, page })}`),
    assignCompanies: (input) =>
      request<void>("/companies/assign/", { method: "POST", body: input }),
    mergeCompanies: (input) =>
      request<{ companyId: number; name: string }>("/companies/merge/", {
        method: "POST",
        body: input,
      }),
  };
}
